import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../../services/axiosComman";

const SERIES = [
  {
    key: "completed",
    label: "Completed",
    statusFilter: "completed",
    color: "#008000", // Green
    hoverColor: "#006600",
    getPct: (item) => item?.completion_percentage ?? item?.completed_percentage ?? 0,
    getCount: (item) => item?.completed_count ?? 0,
  },
  {
    key: "in_progress",
    label: "In Progress",
    statusFilter: "in progress",
    color: "#ffd700", // Yellow / Gold
    hoverColor: "#e6c300",
    getPct: (item) => item?.in_progress_percentage ?? 0,
    getCount: (item) => item?.in_progress_count ?? 0,
  },
  {
    key: "yet_to_start",
    label: "Yet To Start",
    statusFilter: "yet to start",
    color: "#FF0000", // Red
    hoverColor: "#cc0000",
    getPct: (item) => item?.yet_to_start_percentage ?? 0,
    getCount: (item) => item?.yet_to_start_count ?? 0,
  },
];

function DashboardVerticalBarChartView({
  url = "",
  data: propData = null,
  title = "COMPLIANCE BY HEADER - Implementation Checklist",
  standardName = "",
  path = "",
  maxHeight = "460px",
}) {
  const [data, setData] = useState(propData || []);
  const [loading, setLoading] = useState(!propData && Boolean(url));
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const cardRef = useRef(null);
  const navigate = useNavigate();

  async function getFunction() {
    if (!url) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(url);
      if (res?.data?.data && Array.isArray(res.data.data)) {
        setData(res.data.data);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching vertical bar chart data:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (propData) {
      setData(propData);
      setLoading(false);
    } else if (url) {
      getFunction();
    }
  }, [url, propData]);

  const handleBarClick = (item, statusFilter = null) => {
    if (path) {
      navigate(path, {
        state: {
          statusFilter: statusFilter || null,
          headerId: item?.header_id,
          headerName: item?.header_name,
        },
      });
    }
  };

  const formatPercentage = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num <= 0) return "0%";
    return num % 1 === 0 ? `${num.toFixed(0)}%` : `${num.toFixed(1)}%`;
  };

  const getBarWidth = (val) => {
    const num = parseFloat(val);
    if (isNaN(num) || num <= 0) return 0;
    return Math.min(100, Math.max(0, num));
  };

  const displayTitle = standardName
    ? `${title} (${standardName.toUpperCase()})`
    : title;

  return (
    <div
      ref={cardRef}
      className="w-full bg-white rounded-xl shadow-main border border-[#E5E7EB] overflow-hidden flex flex-col relative"
    >
      {/* Top Banner */}
      <div className="bg-[#0B1536] text-white px-5 py-2.5 flex items-center justify-between min-h-[44px]">
        <h2 className="text-[13px] 2xl:text-[14px] font-bold tracking-wide uppercase">
          {displayTitle}
        </h2>
        {path && (
          <Link
            to={path}
            className="text-[11px] 2xl:text-xs text-[#9CA3AF] hover:text-white transition-colors underline"
          >
            View Checklist
          </Link>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-x-6 px-5 pt-3 pb-2 border-b border-gray-100">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center gap-x-2 select-none">
            <span
              className="w-3.5 h-3.5 rounded-full inline-block shadow-sm"
              style={{ backgroundColor: s.color }}
            ></span>
            <span className="text-[12px] font-semibold text-[#4F4768]">
              {s.label}
            </span>
          </div>
        ))}
      </div>

      {/* Floating Tooltip */}
      {hoveredGroup && (
        <div
          style={{
            position: "absolute",
            left: `${hoveredGroup.left}px`,
            top: `${hoveredGroup.top - 8}px`,
            transform: "translate(-50%, -100%)",
            zIndex: 9999,
          }}
          className="bg-[#0B1536] text-white text-[11px] px-3 py-2 rounded-lg shadow-2xl whitespace-nowrap pointer-events-none border border-gray-700 transition-all duration-100"
        >
          <p className="font-bold border-b border-gray-700 pb-1 mb-1 text-center text-gray-200">
            {hoveredGroup.item.header_name}
          </p>
          <div className="space-y-0.5 text-[10px]">
            {SERIES.map((s) => {
              const p = s.getPct(hoveredGroup.item);
              const c = s.getCount(hoveredGroup.item);
              return (
                <div key={s.key} className="flex items-center gap-x-2">
                  <span
                    className="w-2 h-2 rounded-full inline-block"
                    style={{ backgroundColor: s.color }}
                  ></span>
                  <span className="text-gray-300">{s.label}:</span>
                  <span className="font-semibold text-white ml-auto">
                    {c} / {hoveredGroup.item.total_count ?? 0} ({formatPercentage(p)})
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chart Body */}
      <div className="p-5 flex-1 flex flex-col justify-between">
        {loading ? (
          <div className="w-full h-64 flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-[#5945DB]"></div>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="w-full h-64 flex flex-col items-center justify-center text-[#6E6985]">
            <p className="text-sm font-medium">No Header Data Available</p>
          </div>
        ) : (
          <div className="w-3/4 flex flex-col">
            {/* X-Axis Scale Header (0%, 25%, 50%, 75%, 100%) */}
            <div className="w-full flex items-center mb-2 select-none">
              <div className="w-48 sm:w-56 flex-shrink-0 pr-4 text-right">
                <span className="text-[11px] font-semibold text-[#6E6985]">
                  Headers / Categories
                </span>
              </div>
              <div className="flex-1 flex justify-between text-[10px] 2xl:text-[11px] font-semibold text-[#6E6985] px-1">
                <span>0%</span>
                <span className="-translate-x-1/2">25%</span>
                <span className="-translate-x-1/2">50%</span>
                <span className="-translate-x-1/2">75%</span>
                <span className="-translate-x-full">100%</span>
              </div>
            </div>

            {/* Scrollable list of headers */}
            <div
              className="w-full overflow-y-auto h-80 max-h-[380px] pr-2 space-y-3"
              style={{ maxHeight }}
              onScroll={() => setHoveredGroup(null)}
            >
              {data.map((item, index) => (
                <div
                  key={item.header_id || index}
                  className="w-full flex items-center group cursor-pointer hover:bg-gray-50/70 p-2 rounded-lg transition-colors"
                  onClick={() => handleBarClick(item)}
                  onMouseEnter={(e) => {
                    if (cardRef.current) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const parentRect = cardRef.current.getBoundingClientRect();
                      setHoveredGroup({
                        item,
                        left: rect.left - parentRect.left + rect.width / 2,
                        top: rect.top - parentRect.top,
                      });
                    }
                  }}
                  onMouseLeave={() => {
                    setHoveredGroup(null);
                    setHoveredBar(null);
                  }}
                >
                  {/* Header Title (Y-Axis) */}
                  <div className="w-48 sm:w-56 flex-shrink-0 pr-4 flex flex-col justify-center">
                    <span
                      className="text-[12px] 2xl:text-[13px] font-medium text-[#4F4768] group-hover:text-primary transition-colors leading-tight line-clamp-2"
                      title={item.header_name}
                    >
                      {item.header_name}
                    </span>
                    {item.total_count !== undefined && (
                      <span className="text-[10px] text-[#9CA3AF] mt-0.5">
                        Total: {item.total_count} items
                      </span>
                    )}
                  </div>

                  {/* Horizontal Bars Container with Vertical Gridlines */}
                  <div className="flex-1 relative flex flex-col justify-center gap-y-1.5 py-1">
                    {/* Background Gridlines */}
                    <div className="absolute inset-0 pointer-events-none flex justify-between z-0">
                      <div className="h-full border-r border-gray-200"></div>
                      <div className="h-full border-r border-gray-200 border-dashed"></div>
                      <div className="h-full border-r border-gray-200 border-dashed"></div>
                      <div className="h-full border-r border-gray-200 border-dashed"></div>
                      <div className="h-full border-r border-gray-200"></div>
                    </div>

                    {/* Bars for 3 series */}
                    {SERIES.map((s) => {
                      const pct = s.getPct(item);
                      const barWidth = getBarWidth(pct);
                      const numPct = parseFloat(pct) || 0;
                      const isThisBarHovered =
                        hoveredBar?.header_id === item.header_id &&
                        hoveredBar?.key === s.key;

                      return (
                        <div
                          key={s.key}
                          className="w-full flex items-center relative z-10 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBarClick(item, s.statusFilter);
                          }}
                          onMouseEnter={(e) => {
                            e.stopPropagation();
                            setHoveredBar({ header_id: item.header_id, key: s.key });
                          }}
                        >
                          {/* Track and Bar with Percentage Near the Bar */}
                          <div className="flex-1 h-[12px] relative shadow-inner">
                            <div
                              className="h-full transition-all duration-300 ease-out"
                              style={{
                                width: `${barWidth}%`,
                                backgroundColor: isThisBarHovered
                                  ? s.hoverColor
                                  : s.color,
                                minWidth: numPct > 0 ? "4px" : "0px",
                              }}
                            />

                            {/* Percentage label directly near the bar */}
                            {/* {barWidth > 85 ? ( */}
                            {/* <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] 2xl:text-[10px] font-bold text-[#0B1536] pointer-events-none select-none whitespace-nowrap drop-shadow-sm"
                                 style={{
                                  // left: `calc(${barWidth}% + 6px)`,
                                  color:
                                    numPct > 0
                                      ? isThisBarHovered
                                        ? s.hoverColor
                                        : s.color
                                      : "#9CA3AF",
                                }}>
                                {formatPercentage(pct)}
                              </span> */}
                            {/* ) : ( */}
                            <span
                              className="absolute top-1/2 -translate-y-1/2 text-[9px] 2xl:text-[10px] font-bold pointer-events-none select-none whitespace-nowrap transition-all duration-300"
                              style={{
                                left: `calc(${barWidth}% + 6px)`,
                                color:
                                  numPct > 0
                                    ? isThisBarHovered
                                      ? s.hoverColor
                                      : s.color
                                    : "#9CA3AF",
                              }}
                            >
                              {formatPercentage(pct)}
                            </span>
                            {/* )} */}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardVerticalBarChartView;
